import React, { useState } from "react";
import { Box, Typography, Switch, useTheme } from "@mui/material";
import { Menu } from "../../Common/Menu";
import {
  Check,
  CloudArrowUp,
  DotsThree,
  PencilSimple,
  SquaresFour,
  Trash,
  Star,
} from "@phosphor-icons/react";
import { useDarkMode } from "../../../hooks/useDarkMode";
import { DashboardMeta } from "../../../views/DashboardBeta/hooks/useDashboardManager";

export interface DashboardSelectorItemProps {
  dashboard: DashboardMeta;
  isActive: boolean;
  onSelect: () => void;
  onRename: (dashboard: DashboardMeta) => void;
  onDelete: (dashboard: DashboardMeta) => void;
  onPublish: (dashboard: DashboardMeta) => void;
  onSetDefault: (dashboard: DashboardMeta) => void;
  canDelete: boolean;
}

export const DashboardSelectorItem: React.FC<DashboardSelectorItemProps> = ({
  dashboard,
  isActive,
  onSelect,
  onRename,
  onDelete,
  onPublish,
  onSetDefault,
  canDelete,
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();

  const [isHovered, setIsHovered] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSelect = () => {
    if (!menuAnchor) {
      onSelect();
    }
  };

  const handleRename = () => {
    handleMenuClose();
    onRename(dashboard);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(dashboard);
  };

  const handlePublish = () => {
    handleMenuClose();
    onPublish(dashboard);
  };

  const handleSetDefault = (e?: React.MouseEvent | React.ChangeEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onSetDefault(dashboard);
  };

  const isPublished = dashboard.status === "published";

  return (
    <Box
      component="button"
      onClick={handleSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        width: "100%",
        minHeight: "46px",
        maxHeight: "46px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        padding: "10px 12px",
        border: "none",
        borderRadius: "6px",
        backgroundColor: isActive
          ? isDarkMode
            ? palette.gray[700]
            : palette.blue[50]
          : "transparent",
        cursor: "pointer",
        textAlign: "left",
        "&:hover": {
          backgroundColor: isDarkMode ? palette.gray[700] : palette.gray[100],
        },
      }}
    >
      <SquaresFour
        size={22}
        color={
          isActive
            ? isDarkMode
              ? palette.blue[100]
              : palette.blue[500]
            : isDarkMode
              ? palette.gray[400]
              : palette.gray[500]
        }
      />
      <Box
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}
      >
        <Typography
          sx={{
            color: isActive
              ? isDarkMode
                ? palette.blue[100]
                : palette.blue[500]
              : isDarkMode
                ? palette.gray[200]
                : palette.gray[700],
            fontFamily: "Roboto",
            fontSize: "14px",
            fontWeight: isActive ? 500 : 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {dashboard.name}
        </Typography>
        {dashboard.status === "draft" && (
          <Typography
            sx={{
              color: isDarkMode ? palette.gray[500] : palette.gray[400],
              fontFamily: "Roboto",
              fontSize: "10px",
              fontWeight: 400,
              textTransform: "capitalize",
            }}
          >
            {dashboard.status}
          </Typography>
        )}
      </Box>

      {isHovered && (
        <Box
          component="button"
          onClick={handleMenuClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
            height: "24px",
            padding: 0,
            border: "none",
            borderRadius: "4px",
            backgroundColor: "transparent",
            cursor: "pointer",
            flexShrink: 0,
            color: isDarkMode ? palette.gray[400] : palette.gray[500],
            "&:hover": {
              backgroundColor: isDarkMode
                ? palette.gray[600]
                : palette.gray[200],
              color: isDarkMode ? palette.gray[200] : palette.gray[700],
            },
          }}
        >
          <DotsThree size={18} weight="bold" />
        </Box>
      )}

      {!isHovered && isActive && (
        <Check
          size={16}
          color={isDarkMode ? palette.blue[100] : palette.blue[500]}
          weight="bold"
        />
      )}

      <Menu
        open={Boolean(menuAnchor)}
        anchorEl={menuAnchor}
        onClose={handleMenuClose}
        items={[
          {
            label: "Set as Default",
            icon: (
              <Star
                size={20}
                color={
                  dashboard.isDefault
                    ? palette.blue[500]
                    : isDarkMode
                      ? palette.gray[400]
                      : palette.gray[500]
                }
                weight={dashboard.isDefault ? "fill" : "regular"}
              />
            ),
            onClick: handleSetDefault,
            customContent: (
              <Switch
                checked={dashboard.isDefault || false}
                onChange={handleSetDefault}
                size="small"
                sx={{
                  marginLeft: "auto",
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: palette.blue[500],
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: palette.blue[500],
                  },
                }}
              />
            ),
          },
          {
            label: "Rename",
            icon: (
              <PencilSimple
                size={20}
                color={isDarkMode ? palette.gray[400] : palette.gray[500]}
              />
            ),
            onClick: handleRename,
          },
          {
            label: isPublished ? "Unpublish" : "Publish",
            icon: (
              <CloudArrowUp
                size={20}
                color={
                  isPublished
                    ? palette.blue[500]
                    : isDarkMode
                      ? palette.gray[400]
                      : palette.gray[500]
                }
              />
            ),
            onClick: handlePublish,
          },
          {
            label: "Delete",
            icon: <Trash size={20} color="#EF4444" />,
            onClick: handleDelete,
            disabled: !canDelete,
            color: "#EF4444",
            hoverBackgroundColor: isDarkMode ? palette.gray[800] : "#FEF2F2",
          },
        ]}
      />
    </Box>
  );
};
